/**
 * Centralized Ambulance Type & Abbreviation Helper (Frontend)
 * Works dynamically with backend data (which now sends `abbreviation`),
 * with intelligent fallback for raw strings or legacy responses.
 */

const KNOWN_ABBREVIATIONS = {
  'Basic Life Support Small': 'BLS Small',
  'Basic Life Support Big': 'BLS Big',
  'Basic Life Support': 'BLS',
  'Basic Life Support (BLS)': 'BLS',
  'Advanced Life Support': 'ALS',
  'Advanced Life Support (ALS)': 'ALS',
  'Advanced Cardiac Life Support': 'ACLS',
  'Critical Care Transport': 'ICU',
  'Neonatal Transport': 'NICU',
  'Pediatric Transport': 'PICU',
  'NICU / PICU': 'NICU/PICU',
  'Neonatal / Pediatric ICU': 'NICU/PICU',
  'Patient Transport (PTV)': 'PTV',
  'Patient Transport': 'PTV',
  'Patient Transport Vehicle': 'PTV',
  'Psychiatric Transport': 'PSYCH',
  'Bariatric Transport': 'BAR',
  'Mortuary': 'Mortuary',
  'Dead Body Transport': 'Mortuary',
  'Air Ambulance': 'AIR',
  'ecco': 'ECMO',
};

/**
 * Returns short abbreviation (e.g. "ALS", "BLS Small", "ACLS")
 */
export const getAmbulanceTypeAbbreviation = (typeOrName) => {
  if (!typeOrName) return '';

  // If passed an object with abbreviation property from backend
  if (typeof typeOrName === 'object') {
    if (typeOrName.abbreviation) return typeOrName.abbreviation;
    typeOrName = typeOrName.name || '';
  }

  const trimmed = String(typeOrName || '').trim();
  if (!trimmed) return '';

  if (KNOWN_ABBREVIATIONS[trimmed]) {
    return KNOWN_ABBREVIATIONS[trimmed];
  }

  const bracketMatch = trimmed.match(/\(([^)]+)\)/);
  if (bracketMatch && bracketMatch[1].trim().length > 0) {
    return bracketMatch[1].trim();
  }

  if (/cardiac/i.test(trimmed)) return 'ACLS';
  if (/small/i.test(trimmed)) {
    return /advanced/i.test(trimmed) ? 'ALS Small' : 'BLS Small';
  }
  if (/big/i.test(trimmed)) {
    return /advanced/i.test(trimmed) ? 'ALS Big' : 'BLS Big';
  }
  if (/^advanced life support/i.test(trimmed)) return 'ALS';
  if (/^basic life support/i.test(trimmed)) return 'BLS';
  if (/critical/i.test(trimmed)) return 'ICU';
  if (/neonatal/i.test(trimmed)) return 'NICU';
  if (/pediatric/i.test(trimmed)) return 'PICU';
  if (/psychiatric/i.test(trimmed)) return 'PSYCH';
  if (/bariatric/i.test(trimmed)) return 'BAR';
  if (/ecco|ecmo/i.test(trimmed)) return 'ECMO';

  const words = trimmed.split(/\s+/).filter((w) => !['and', 'or', 'of', 'for', 'the', '&'].includes(w.toLowerCase()));
  if (words.length > 1 && words.length <= 4) {
    return words.map((w) => w[0].toUpperCase()).join('');
  }

  return trimmed;
};

// Alias for existing callers
export const getAmbulanceTypeShortName = getAmbulanceTypeAbbreviation;

/**
 * Formats ambulance type for display.
 * @param {object|string} typeOrName - Type object or string name
 * @param {'short'|'combined'|'label'} mode
 */
export const formatAmbulanceType = (typeOrName, mode = 'combined') => {
  if (!typeOrName) return '';

  const name = typeof typeOrName === 'object' ? typeOrName.name || '' : String(typeOrName);
  const abbr = getAmbulanceTypeAbbreviation(typeOrName);

  if (mode === 'short') {
    return abbr || name;
  }

  if (mode === 'combined') {
    if (abbr && abbr !== name && !name.includes(`(${abbr})`)) {
      return `${abbr} (${name})`;
    }
    return name;
  }

  return name;
};

export default {
  getAmbulanceTypeAbbreviation,
  getAmbulanceTypeShortName,
  formatAmbulanceType,
};
